// src/components/homeComponents/ProcessReel.jsx
import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const ACTS = [
  {
    no: "01",
    label: "PRE-PRODUCTION",
    title: "We find the obsession.",
    body: "Before a single shot, we hunt for the one thing that makes people lean in.",
  },
  {
    no: "02",
    label: "THE SCRIPT",
    title: "Hooks, angles, story.",
    body: "Every scroll-stopping second is written first — the hook earns the next frame.",
  },
  {
    no: "03",
    label: "THE SHOOT",
    title: "Cinematic content & code.",
    body: "We produce the films and build the sites that hold the attention we captured.",
  },
  {
    no: "04",
    label: "THE PREMIERE",
    title: "Ship. Watch. Sell.",
    body: "We launch, read the numbers, and re-cut until it converts on repeat.",
  },
];

const ProcessReel = () => {
  const sectionRef = useRef(null);
  const lineRef = useRef(null);
  const actRefs = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const line = lineRef.current;
      if (line) {
        const len = line.getTotalLength();
        gsap.set(line, { strokeDasharray: len, strokeDashoffset: len });
        gsap.to(line, {
          strokeDashoffset: 0,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 60%",
            end: "bottom 80%",
            scrub: 1,
          },
        });
      }

      actRefs.current.forEach((el) => {
        if (!el) return;
        gsap.fromTo(
          el,
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 78%" },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section ref={sectionRef}>
      <div className="head">
        <span className="kicker">HOW THE FILM GETS MADE</span>
        <h2>
          We don't post. <br /> We <em>produce.</em>
        </h2>
      </div>

      <div className="reel">
        <svg className="thread" preserveAspectRatio="none" viewBox="0 0 100 1000">
          <path
            ref={lineRef}
            d="M50 0 C 18 160, 82 320, 50 480 S 18 800, 50 1000"
            fill="none"
            stroke="url(#grad)"
            strokeWidth="2"
          />
          <defs>
            <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#9281dd" />
              <stop offset="100%" stopColor="#b9c5fb" />
            </linearGradient>
          </defs>
        </svg>

        {ACTS.map((a, i) => (
          <div
            className={`act ${i % 2 ? "right" : "left"}`}
            key={a.no}
            ref={(el) => (actRefs.current[i] = el)}
          >
            <span className="no">{a.no}</span>
            <div className="card">
              <span className="label">{a.label}</span>
              <h3>{a.title}</h3>
              <p>{a.body}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

const Section = styled.section`
  background: #08040c;
  padding: 8em 7% 10em;
  position: relative;

  .head {
    text-align: center;
    margin-bottom: 4em;
    .kicker {
      font-family: "Montserrat";
      letter-spacing: 0.45em;
      font-size: 0.72rem;
      color: #836caf;
      font-weight: 500;
    }
    h2 {
      font-family: "Readex Pro", sans-serif;
      font-weight: 200;
      font-size: clamp(2rem, 5vw, 4.5rem);
      line-height: 0.95;
      margin-top: 0.4em;
      em {
        font-style: italic;
        color: #9281dd;
      }
    }
  }

  .reel {
    position: relative;
    max-width: 980px;
    margin: auto;
  }
  .thread {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 100%;
    z-index: 0;
    pointer-events: none;
  }

  .act {
    position: relative;
    z-index: 1;
    width: 50%;
    margin-bottom: 9em;
    display: flex;
    gap: 1em;
    &.left {
      margin-right: auto;
      justify-content: flex-end;
      text-align: right;
      flex-direction: row-reverse;
    }
    &.right {
      margin-left: auto;
      text-align: left;
    }
    .no {
      font-family: "Readex Pro", sans-serif;
      font-weight: 200;
      font-size: 3rem;
      color: #2c2342;
      line-height: 1;
    }
    .card {
      .label {
        font-family: "Montserrat";
        letter-spacing: 0.25em;
        font-size: 0.66rem;
        color: #9281dd;
        font-weight: 600;
      }
      h3 {
        font-family: "Readex Pro", sans-serif;
        font-weight: 300;
        font-size: clamp(1.4rem, 2.4vw, 2.2rem);
        margin: 0.3em 0;
        line-height: 1.05;
      }
      p {
        color: #8a8595;
        font-size: 0.98rem;
        line-height: 1.6;
        max-width: 34ch;
      }
    }
  }

  @media (max-width: 760px) {
    .thread {
      left: 14px;
      transform: none;
      width: 60px;
    }
    .act {
      width: 100%;
      padding-left: 42px;
      text-align: left !important;
      flex-direction: row !important;
      justify-content: flex-start !important;
      margin-bottom: 5em;
      .card p {
        max-width: none;
      }
    }
  }
`;

export default ProcessReel;
